import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Clock, Repeat, Briefcase, Headphones, UserCheck, Brain, Wrench } from 'lucide-react'
import ProcessSteps from '../components/ProcessSteps'
import CTASection from '../components/CTASection'

const PageHeader = styled.section`
  padding: calc(var(--header-height) + 64px) 24px 64px;
  text-align: center;
  position: relative;
`

const HeaderGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, var(--color-accent-glow) 0%, transparent 60%);
  pointer-events: none;
`

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
`

const Eyebrow = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 16px;
`

const PageTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
  line-height: 1.15;
`

const PageSubtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
`

const PackagesSection = styled.section`
  padding: 64px 24px 96px;
`

const PackagesInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const SectionLabel = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 12px;
  text-align: center;
`

const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-bottom: 56px;
  text-align: center;
`

const PackageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin: 0 auto;
  }
`

const PackageCard = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid ${({ $featured }) => ($featured ? 'var(--color-accent)' : 'var(--color-border)')};
  border-radius: 16px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.3s ease, transform 0.2s ease;

  ${({ $featured }) => $featured && `box-shadow: 0 0 40px var(--color-accent-glow);`}

  &:hover {
    transform: translateY(-4px);
    border-color: var(--color-accent);
  }
`

const PackageIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--color-accent-glow);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
  margin-bottom: 20px;
`

const PackageName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 8px;
`

const PackagePrice = styled.p`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-success);
  margin-bottom: 16px;
`

const PackageDesc = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  flex: 1;
`

const PackageCTA = styled.a`
  display: block;
  text-align: center;
  padding: 14px 24px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: 24px;
  transition: all 0.2s ease;
  background: ${({ $featured }) =>
    $featured
      ? 'linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end))'
      : 'transparent'};
  color: ${({ $featured }) => ($featured ? 'white' : 'var(--color-text)')};
  border: ${({ $featured }) => ($featured ? 'none' : '1px solid var(--color-border)')};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    color: ${({ $featured }) => ($featured ? 'white' : 'var(--color-text)')};
  }
`

const ImplementSection = styled.section`
  padding: 96px 24px;
  background: var(--color-bg-elevated);
`

const ImplementInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const ImplementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const ImplementCard = styled(motion.div)`
  padding: 28px;
  border-radius: 16px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  transition: border-color 0.3s ease;

  &:hover {
    border-color: var(--color-accent);
  }
`

const ImplementIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-accent-glow);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
  margin-bottom: 16px;
`

const ImplementTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
`

const AgentPrice = styled.p`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--color-success);
  margin-bottom: 12px;
`

const ImplementDesc = styled.p`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
`

const PlatformNote = styled(motion.div)`
  max-width: 700px;
  margin: 56px auto 0;
  text-align: center;
  padding: 28px 32px;
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.7;

  strong {
    color: var(--color-text);
  }
`

const PACKAGES = [
  {
    icon: <Clock size={22} />,
    name: 'Discovery',
    price: 'Free',
    desc: '30-minute call. We learn your business, recommend the right agent type, and give you an honest assessment; even if that assessment is "you don\'t need us."',
    featured: false,
  },
  {
    icon: <Repeat size={22} />,
    name: 'Retainer',
    price: '$2K – $10K/mo',
    desc: 'We configure and deploy your AI agent, then continuously optimize it as your business evolves. Includes persona tuning, knowledge base updates, analytics review, and ongoing support.',
    featured: true,
  },
  {
    icon: <Briefcase size={22} />,
    name: 'Project-Based',
    price: '$5K – $25K',
    desc: 'Defined scope, delivered in 2-8 weeks. Best for launching a specific agent: customer support for your BPO, lead qualification for your brokerage, or a coaching agent for your practice.',
    featured: false,
  },
]

const AGENT_TYPES = [
  {
    icon: <Headphones size={22} />,
    title: 'Support Agent',
    price: '$300 – $800/mo',
    desc: 'Handles FAQs, routes complex issues to humans, and learns from your knowledge base. Supports multilingual responses including Taglish. Ideal for BPOs, service businesses, and e-commerce.',
  },
  {
    icon: <UserCheck size={22} />,
    title: 'Lead & Intake Agent',
    price: '$500 – $1,500/mo',
    desc: 'Qualifies buyers and clients with smart discovery questions, books appointments, and captures leads 24/7. Built for real estate, law firms, med spas, and any appointment-driven business.',
  },
  {
    icon: <Brain size={22} />,
    title: 'Coaching Agent',
    price: '$800 – $2,000/mo',
    desc: 'Extends your coaching practice between live sessions. Custom discovery flows, goal tracking, reflective questioning, and accountability check-ins; all in your voice and methodology.',
  },
  {
    icon: <Wrench size={22} />,
    title: 'Custom Agent',
    price: 'Contact Us',
    desc: 'Multi-capability agents with custom integrations, voice AI, social content automation, and enterprise features. We design and build the agent around your specific business needs.',
  },
]

export default function Services() {
  return (
    <>
      <PageHeader>
        <HeaderGlow />
        <HeaderContent>
          <Eyebrow>Services</Eyebrow>
          <PageTitle>High-touch implementation, powered by AI</PageTitle>
          <PageSubtitle>
            We don't just advise. We show up, understand your business, configure AI agents
            that actually work for you, and stay with you as you grow.
          </PageSubtitle>
        </HeaderContent>
      </PageHeader>

      <PackagesSection>
        <PackagesInner>
          <SectionLabel>Engagement Models</SectionLabel>
          <SectionTitle>Choose how we work together</SectionTitle>
          <PackageGrid>
            {PACKAGES.map((pkg, i) => (
              <PackageCard
                key={i}
                $featured={pkg.featured}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <PackageIcon>{pkg.icon}</PackageIcon>
                <PackageName>{pkg.name}</PackageName>
                <PackagePrice>{pkg.price}</PackagePrice>
                <PackageDesc>{pkg.desc}</PackageDesc>
                <PackageCTA
                  href="https://calendly.com/coybyron/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  $featured={pkg.featured}
                >
                  {pkg.featured ? 'Book Discovery Call' : 'Learn More'}
                </PackageCTA>
              </PackageCard>
            ))}
          </PackageGrid>
        </PackagesInner>
      </PackagesSection>

      <ImplementSection>
        <ImplementInner>
          <SectionLabel>Agent Types</SectionLabel>
          <SectionTitle>Pick the agent that fits your business</SectionTitle>
          <ImplementGrid>
            {AGENT_TYPES.map((agent, i) => (
              <ImplementCard
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <ImplementIcon>{agent.icon}</ImplementIcon>
                <ImplementTitle>{agent.title}</ImplementTitle>
                <AgentPrice>{agent.price}</AgentPrice>
                <ImplementDesc>{agent.desc}</ImplementDesc>
              </ImplementCard>
            ))}
          </ImplementGrid>
          <PlatformNote
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <strong>Powered by the same platform behind DoMoreWorld</strong>; a production-grade
            AI platform with provider-agnostic chat, function-calling tools, voice, and community
            infrastructure. We configure it for your business, we don't build from scratch.
          </PlatformNote>
        </ImplementInner>
      </ImplementSection>

      <ProcessSteps />

      <CTASection
        title="Ready to start?"
        subtitle="Book a free discovery call. We'll assess your needs and give you an honest recommendation."
        buttonText="Book a Discovery Call"
      />
    </>
  )
}
